import { type } from 'arktype'

import type { FrMrgaFleetVehicleDeductible, FrMrgaFleetVehicleFormula, FrMrgaFleetVehicleUse } from './fr-mrga-fleet.ts'

// One policy item per guarantee, each commented with its name in the general terms (CG).
export const frMrgaPolicyItems = [
  // Responsabilité civile exploitation
  'publicLiability',
  // Responsabilité civile après livraison ou après réception
  'productsAndCompletedWorksLiability',
  // Incendie - Dommages assimilés
  'fire',
  // Dommages électriques
  'electricalDamage',
  // Tempête - Grêle - Neige
  'climateEvents',
  // Catastrophes naturelles
  'naturalDisasters',
  // Attentats - Émeutes et dommages assimilés
  'terrorism',
  // Dégâts des liquides
  'waterDamage',
  // Détériorations immobilières suite à vol, tentative de vol ou vandalisme
  'buildingDamageAfterTheft',
  // Vol / Vandalisme
  'theft',
  // Bris de glace
  'glass',
  // Bris de machine et matériel informatique
  'machineryAndComputerBreakage',
  // Perte d'exploitation
  'businessInterruption',
  // Responsabilité civile circulation (véhicules propriété et confiés)
  'civilLiability',
  // Défense pénale et recours
  'criminalDefenseAndRecourse',
  // Garantie conducteur
  'driverProtection',
  // Dommages aux véhicules assurés (véhicules propriété)
  'ownedVehiclesDamage',
  // Effets et objets personnels / Accessoires hors série
  'personalBelongingsAndAccessories',
  // Dommages aux véhicules destinés à la vente
  'vehiclesForSaleDamage',
  // Dommages aux véhicules confiés
  'entrustedVehiclesDamage',
  // Protection juridique
  'legalProtection',
  // Assistance
  'assistance',
] as const
const frMrgaPolicyItemType = type('===', ...frMrgaPolicyItems)
export type FrMrgaPolicyItem = typeof frMrgaPolicyItemType.infer

export function isFrMrgaPolicyItem(policyItemName: string): policyItemName is FrMrgaPolicyItem {
  return frMrgaPolicyItemType.allows(policyItemName)
}

export type FrMrgaPolicyItemsFleetVehicle = {
  use?: FrMrgaFleetVehicleUse | null | undefined
  formula?: FrMrgaFleetVehicleFormula | null | undefined
  deductible?: FrMrgaFleetVehicleDeductible | null | undefined
}

// The inputs the availability, limits and deductibles depend on.
export type FrMrgaPolicyItemsState = {
  frMrgaContentCapital?: number | null | undefined
  frMrgaVehiclesLimitOfIndemnity?: number | null | undefined
  frMrgaFleet?: readonly FrMrgaPolicyItemsFleetVehicle[] | null | undefined
  frMrgaHasVehiclesForSale?: boolean | null | undefined
}

/**
 * A policy item either applies to the contract whatever its fleet, is granted to the vehicles of the fleet whose use
 * and formula both call for it, or applies when the garage declares vehicles for sale, which the fleet does not list.
 */
export type FrMrgaPolicyItemScope =
  | { type: 'contract' }
  | {
      type: 'vehicle'
      uses: Record<FrMrgaFleetVehicleUse, boolean>
      formulas: Record<FrMrgaFleetVehicleFormula, boolean>
    }
  | { type: 'vehiclesForSale' }

type FrMrgaPolicyItemScopeVehicle = Pick<FrMrgaPolicyItemsFleetVehicle, 'use' | 'formula'>

/**
 * Only a vehicle policy item is granted to a vehicle of the fleet, and a vehicle whose use or formula is not chosen yet
 * gets none.
 */
export function isFrMrgaPolicyItemGrantedToVehicle(
  scope: FrMrgaPolicyItemScope,
  { use, formula }: FrMrgaPolicyItemScopeVehicle,
): boolean {
  switch (scope.type) {
    case 'contract':
    case 'vehiclesForSale':
      return false
    case 'vehicle':
      return !!use && !!formula && scope.uses[use] && scope.formulas[formula]
  }
}

export function isFrMrgaPolicyItemAvailable(
  scope: FrMrgaPolicyItemScope,
  { frMrgaFleet, frMrgaHasVehiclesForSale }: Pick<FrMrgaPolicyItemsState, 'frMrgaFleet' | 'frMrgaHasVehiclesForSale'>,
): boolean {
  switch (scope.type) {
    case 'contract':
      return true
    case 'vehicle':
      return (frMrgaFleet ?? []).some((vehicle) => isFrMrgaPolicyItemGrantedToVehicle(scope, vehicle))
    case 'vehiclesForSale':
      return !!frMrgaHasVehiclesForSale
  }
}
